import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import "./connections.css";
import { supabase } from "../../supabase_client.jsx";

function getId(u) {
  return u?.user_id ?? u?.id ?? u?.uuid ?? null;
}

function safeArray(x) {
  return Array.isArray(x) ? x : [];
}

function intersectionCount(a, b) {
  const A = new Set(safeArray(a));
  let cnt = 0;
  for (const v of safeArray(b)) if (A.has(v)) cnt++;
  return cnt;
}

export default function Connections({ currentUser, onViewSelect }) {
  const currentUserId = currentUser?.user?.id ?? currentUser?.id ?? null;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const loadedOnceRef = useRef(false);

  const [errorMsg, setErrorMsg] = useState("");

  const [users, setUsers] = useState([]); 
  const [metaByUserId, setMetaByUserId] = useState(new Map());
  const [requests, setRequests] = useState([]);

  const [connections, setConnections] = useState([]);
  const [incomingPending, setIncomingPending] = useState([]);
  const [outgoingPending, setOutgoingPending] = useState([]);
  const [suggested, setSuggested] = useState([]);

  // ===== Safety confirmation modal state =====
  const [showSafetyModal, setShowSafetyModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  // pendingAction: { type: "ADD" | "ACCEPT", receiverId?: string, requestId?: number }

  // ===== Connection actions (3-dot menu) =====
  const [openActionsFor, setOpenActionsFor] = useState(null); // userId
  const [confirmRemoveUser, setConfirmRemoveUser] = useState(null); // user object (after report)

  // ===== Report modal =====
  const [reportTarget, setReportTarget] = useState(null); // user object being reported
  const [reportReason, setReportReason] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState("");

  const displayName = useCallback(
    (u) => {
      const id = getId(u);
      const meta = id ? metaByUserId.get(id) : null;
      const metaName = (meta?.name || "").trim();
      if (metaName) return metaName;

      return u?.username || u?.email || id || "Unknown User";
    },
    [metaByUserId]
  );

  const currentPrefs = useMemo(() => {
    if (!currentUserId) return [];
    const meta = metaByUserId.get(currentUserId);
    return safeArray(meta?.preferences);
  }, [currentUserId, metaByUserId]);

  const fetchUsers = useCallback(async () => {
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .eq("type", "User")
      .limit(500);

    if (error) {
      console.error("fetchUsers failed:", error);
      return [];
    }
    return data || [];
  }, []);

  const fetchProfileMeta = useCallback(async () => {
    const { data, error } = await supabase
      .from("user_profile_meta")
      .select("user_id,name,preferences,has_setup_profile")
      .limit(1000);

    if (error || !Array.isArray(data)) return new Map();

    const m = new Map();
    for (const row of data) {
      if (row.user_id) m.set(row.user_id, row);
    }
    return m;
  }, []);

  const fetchRequests = useCallback(async () => {
    if (!currentUserId) return [];
    const { data, error } = await supabase
      .from("connection_requests")
      .select("*")
      .or(`requester_id.eq.${currentUserId},receiver_id.eq.${currentUserId}`)
      .limit(2000);

    if (error) {
      console.error("fetchRequests failed:", error);
      return [];
    }
    return data || [];
  }, [currentUserId]);

  const rebuildDerivedLists = useCallback(
    (allUsers, metaMap, reqRows, myPrefs) => {
      const idToUser = new Map();
      for (const u of allUsers) {
        const id = getId(u);
        if (id) idToUser.set(id, u);
      }

      const acceptedIds = new Set();
      const incomingIds = new Set();
      const outgoingIds = new Set();
      const blockedIds = new Set();

      for (const r of reqRows) {
        const a = r.requester_id;
        const b = r.receiver_id;
        const status = (r.status || "pending").toLowerCase();
        if (!a || !b) continue;

        const other = a === currentUserId ? b : b === currentUserId ? a : null;
        if (!other) continue;

        blockedIds.add(other);

        if (status === "accepted") acceptedIds.add(other);
        if (status === "pending") {
          if (r.receiver_id === currentUserId) incomingIds.add(r.requester_id);
          if (r.requester_id === currentUserId) outgoingIds.add(r.receiver_id);
        }
      }

      const acceptedUsers = [...acceptedIds].map((id) => idToUser.get(id)).filter(Boolean);
      const incomingUsers = [...incomingIds].map((id) => idToUser.get(id)).filter(Boolean);
      const outgoingUsers = [...outgoingIds].map((id) => idToUser.get(id)).filter(Boolean);

      const suggestions = [];
      for (const [id, u] of idToUser.entries()) {
        if (!id) continue;
        if (id === currentUserId) continue;
        if (blockedIds.has(id)) continue;

        const theirMeta = metaMap.get(id);
        const theirPrefs = safeArray(theirMeta?.preferences);
        const score = intersectionCount(myPrefs, theirPrefs);

        suggestions.push({
          user: u,
          userId: id,
          score,
          common: safeArray(myPrefs).filter((p) => theirPrefs.includes(p)),
        });
      }

      suggestions.sort((x, y) => {
        if (y.score !== x.score) return y.score - x.score;
        const xn = (metaMap.get(x.userId)?.name || x.user.username || "").toLowerCase();
        const yn = (metaMap.get(y.userId)?.name || y.user.username || "").toLowerCase();
        return xn.localeCompare(yn);
      });

      setConnections(acceptedUsers);
      setIncomingPending(incomingUsers);
      setOutgoingPending(outgoingUsers);
      setSuggested(suggestions.slice(0, 12));
    },
    [currentUserId]
  );

  const loadAll = useCallback(async () => {
    if (!currentUserId) return;

    const isFirst = !loadedOnceRef.current;
    if (isFirst) setLoading(true);
    else setRefreshing(true);

    setErrorMsg("");

    try {
      const [u, metaMap, req] = await Promise.all([
        fetchUsers(),
        fetchProfileMeta(),
        fetchRequests(),
      ]);

      setUsers(u);
      setMetaByUserId(metaMap);
      setRequests(req);

      const myPrefs = safeArray(metaMap.get(currentUserId)?.preferences);
      rebuildDerivedLists(u, metaMap, req, myPrefs);

      loadedOnceRef.current = true;
    } catch (e) {
      console.warn("Connections load error:", e);
      setErrorMsg("Failed to load connections.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentUserId, fetchUsers, fetchProfileMeta, fetchRequests, rebuildDerivedLists]);

  useEffect(() => {
    if (!currentUserId) return;
    loadAll();
  }, [currentUserId]);

  // ========= Low-level actions  =========
  async function doSendRequest(receiverId) {
    if (!currentUserId || !receiverId) {
      setErrorMsg("Could not send request (missing user id).");
      return;
    }

    setErrorMsg("");

    // If they already requested you, accept instead
    const reverse = requests.find(
      (r) =>
        r.requester_id === receiverId &&
        r.receiver_id === currentUserId &&
        (r.status || "").toLowerCase() === "pending"
    );

    if (reverse) {
      await doAcceptRequest(reverse.id);
      return;
    }

    // Check if a request already exists in either direction before inserting
    const { data: existing } = await supabase
      .from("connection_requests")
      .select("id, status")
      .or(
        `and(requester_id.eq.${currentUserId},receiver_id.eq.${receiverId}),and(requester_id.eq.${receiverId},receiver_id.eq.${currentUserId})`
      )
      .limit(1);

    if (existing && existing.length > 0) {
      // Request already exists — just refresh and move on silently
      await loadAll();
      return;
    }

    const { error } = await supabase.from("connection_requests").insert({
      requester_id: currentUserId,
      receiver_id: receiverId,
      status: "pending",
    });

    if (error) {
      console.error("Send request failed:", error);
      setErrorMsg(error.message);
      return;
    }

    await loadAll();
  }

  async function doAcceptRequest(requestId) {
    setErrorMsg("");
    const { error } = await supabase
      .from("connection_requests")
      .update({ status: "accepted" })
      .eq("id", requestId);

    if (error) {
      console.error("Accept failed:", error);
      setErrorMsg(error.message);
    }
    await loadAll();
  }

  async function rejectRequest(requestId) {
    setErrorMsg("");
    const { error } = await supabase
      .from("connection_requests")
      .update({ status: "rejected" })
      .eq("id", requestId);

    if (error) {
      console.error("Reject failed:", error);
      setErrorMsg(error.message);
    }
    await loadAll();
  }

  async function cancelOutgoing(receiverId) {
    setErrorMsg("");
    const { error } = await supabase
      .from("connection_requests")
      .delete()
      .eq("requester_id", currentUserId)
      .eq("receiver_id", receiverId)
      .eq("status", "pending");

    if (error) {
      console.error("Cancel failed:", error);
      setErrorMsg(error.message);
    }
    await loadAll();
  }

async function removeConnection(otherUserId) {
  if (!currentUserId || !otherUserId) return;
  setErrorMsg("");
  // Find the accepted connection row in either direction, then delete it
  const { data: rows, error: findErr } = await supabase
    .from("connection_requests")
    .select("id")
    .eq("status", "accepted")
    .or(
      `and(requester_id.eq.${currentUserId},receiver_id.eq.${otherUserId}),and(requester_id.eq.${otherUserId},receiver_id.eq.${currentUserId})`
    )
    .limit(1);

  if (findErr) {
    console.error("Find accepted connection failed:", findErr);
    alert("Could not remove connection. Please try again.");
    return;
  }

  const rowId = rows?.[0]?.id;
  if (!rowId) return;

  const { error: delErr } = await supabase
    .from("connection_requests")
    .delete()
    .eq("id", rowId);

  if (delErr) {
    console.error("Remove connection failed:", delErr);
    alert("Could not remove connection. Please try again.");
    return;
  }

  await loadAll();
}


  // ========= UI handlers that show safety modal =========
  function requestAdd(receiverId) {
    const action = { type: "ADD", receiverId };
    if (connections?.length === 0 && outgoingPending?.length === 0) {
      setPendingAction(action);
      setShowSafetyModal(true);
    } else {
      doSendRequest(receiverId);
    }
  }

  function requestAccept(requestId) {
    const action = { type: "ACCEPT", requestId };
    if (connections?.length === 0 && outgoingPending?.length === 0) {
      setPendingAction(action);
      setShowSafetyModal(true);
    } else {
      doAcceptRequest(requestId);
    }
  }

  async function confirmSafetyContinue() {
    if (!pendingAction) return;

    const action = pendingAction;
    setShowSafetyModal(false);
    setPendingAction(null);

    if (action.type === "ADD") {
      await doSendRequest(action.receiverId);
    } else if (action.type === "ACCEPT") {
      await doAcceptRequest(action.requestId);
    }
  }

  function cancelSafety() {
    setShowSafetyModal(false);
    setPendingAction(null);
  }

  // ========= Report user =========
  async function submitReport() {
    if (!reportTarget || !reportReason.trim()) return;
    setReportSubmitting(true);
    setReportError("");

    const targetId = getId(reportTarget); // UUID from user_id field
    
    try {
      // 1. Insert into user_reports
      const { error: reportErr } = await supabase.from("user_reports").insert({
        user_id: targetId,
        reported_by: currentUserId,
        reason: reportReason.trim(),
        status: "pending",
      });

      if (reportErr) {
        console.error("Report insert error:", reportErr);
        setReportError(`Failed to submit report: ${reportErr.message}`);
        setReportSubmitting(false);
        return;
      }

      // 2. Mark the user as reported in users table
      await supabase
        .from("users")
        .update({ is_reported: true })
        .eq("user_id", targetId);

      // 3. Close report modal and ask about removing
      setReportTarget(null);
      setReportReason("");
      setReportSubmitting(false);
      setConfirmRemoveUser(reportTarget);
    } catch (err) {
      console.error("Report error:", err);
      setReportError("Unexpected error. Please try again.");
      setReportSubmitting(false);
    }
  }

  const incomingRequestIdByUser = useMemo(() => {
    const m = new Map();
    for (const r of requests) {
      if ((r.status || "").toLowerCase() !== "pending") continue;
      if (r.receiver_id === currentUserId) m.set(r.requester_id, r.id);
    }
    return m;
  }, [requests, currentUserId]);

  return (
    <div className="connections-container">
      {/* ===== Safety Modal ===== */}
      {showSafetyModal && (
        <div className="safety-overlay" role="dialog" aria-modal="true">
          <div className="safety-modal">
            <div className="safety-title">Safety reminder</div>
            <div className="safety-text">
              Be careful when connecting with people you don’t know. Don’t share personal
              information like your address, passwords, financial info, or private details.
              If something feels off, don’t continue.
            </div>

            <div className="safety-actions">
              <button className="connection-btn secondary" onClick={cancelSafety}>
                Cancel
              </button>
              <button className="connection-btn" onClick={confirmSafetyContinue}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

{/* ===== Report -> Remove confirm modal ===== */}
{confirmRemoveUser && (
  <div className="safety-overlay" role="dialog" aria-modal="true">
    <div className="safety-modal">
      <div className="safety-title">Remove connection?</div>
      <div className="safety-text">
        Would you like to remove this user?
      </div>
      <div className="safety-actions">
        <button
          className="connection-btn secondary"
          onClick={() => setConfirmRemoveUser(null)}
        >
          Cancel
        </button>
        <button
          className="connection-btn danger"
          onClick={async () => {
            const id = getId(confirmRemoveUser);
            setConfirmRemoveUser(null);
            await removeConnection(id);
          }}
        >
          Remove user
        </button>
      </div>
    </div>
  </div>
)}

{/* ===== Report User Modal ===== */}
{reportTarget && (
  <div className="safety-overlay" role="dialog" aria-modal="true">
    <div className="safety-modal" style={{ width: 480 }}>
      <div className="safety-title">Report User</div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "12px 0" }}>
        <div className="connection-avatar" style={{ width: 36, height: 36, flexShrink: 0 }}>
          {displayName(reportTarget).charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{displayName(reportTarget)}</div>
          {reportTarget.username && (
            <div style={{ fontSize: 13, color: "#6b7280" }}>@{reportTarget.username}</div>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
          Reason for report <span style={{ color: "#ef4444" }}>*</span>
        </label>
        <textarea
          rows={4}
          placeholder="Describe why you're reporting this user..."
          value={reportReason}
          onChange={(e) => setReportReason(e.target.value)}
          style={{
            width: "100%",
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #d1d5db",
            fontSize: 14,
            resize: "vertical",
            fontFamily: "inherit",
            boxSizing: "border-box",
            background: "#fafafa",
          }}
        />
      </div>

      {reportError && (
        <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 8 }}>{reportError}</div>
      )}

      <div className="safety-actions">
        <button
          className="connection-btn secondary"
          onClick={() => { setReportTarget(null); setReportReason(""); setReportError(""); }}
          disabled={reportSubmitting}
        >
          Cancel
        </button>
        <button
          className="connection-btn danger"
          onClick={submitReport}
          disabled={!reportReason.trim() || reportSubmitting}
        >
          {reportSubmitting ? "Submitting…" : "Submit Report"}
        </button>
      </div>
    </div>
  </div>
)}

      <div className="home-card">
          {/* App title banner */}
          <div className="app-title-banner">
            <h1 className="app-title-text">Do You Wanna Go?</h1>
          </div>

        <div className="calendar-top-nav">
          <div className="nav-left">
            <button className="nav-tab" onClick={() => onViewSelect("user")}>
              Calendar
            </button>
            <button className="nav-tab" onClick={() => onViewSelect("my-events")}>
              My Events
            </button>
            <button className="nav-tab active">My Connections</button>
          </div>

          <div className="nav-right">
            <button
              type="button"
              className="nav-icon-circle nav-icon-button nav-notification-button"
              aria-label="Notifications"
              onClick={() => onViewSelect("notifications")}
            >
              <svg
                className="nav-bell-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 8a6 6 0 10-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
                <path d="M13.73 21a2 2 0 01-3.46 0" />
              </svg>
            </button>

            <div className="nav-avatar-circle">
              <button
                className="nav-tab nav-profile-button"
                onClick={() => onViewSelect("user-profile")}
                aria-label="Profile"
              >
                <svg
                  className="nav-user-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="connections-layout">
          <div className="connections-main">
            <h1 className="connections-title">My Connections</h1>
            <p className="connections-subtitle">
              Friends and people you’ve connected with.
              {refreshing ? <span className="refreshing-pill">Refreshing…</span> : null}
            </p>

            {errorMsg ? <div className="connections-empty">{errorMsg}</div> : null}

            {loading ? (
              <div className="connections-empty">Loading...</div>
            ) : connections.length === 0 ? (
              <div className="connections-empty">
                No connections yet.
                <div className="connections-empty-hint">
                  Send a request from the Suggested Friends list.
                </div>
              </div>
            ) : (
              <div className="connections-list">
                {connections.map((u, idx) => (
                  <div className="connection-card" key={getId(u) ?? idx}>
                    <div className="connection-avatar">
                      {displayName(u).charAt(0).toUpperCase()}
                    </div>
                    <div className="connection-info">
                      <div className="connection-name">{displayName(u)}</div>
                      <div className="connection-meta">
                        {u.username ? `@${u.username}` : u.email || ""}
                      </div>
                    </div>
                    
<div className="connection-actions">
  <button
    type="button"
    className="connection-btn secondary connection-more-btn"
    aria-label="More actions"
    onClick={(e) => {
      e.stopPropagation();
      const id = getId(u);
      setOpenActionsFor((prev) => (prev === id ? null : id));
    }}
  >
    …
  </button>

  {openActionsFor === getId(u) && (
    <div
      className="connection-actions-menu"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="connection-actions-item"
        onClick={async (e) => {
          e.stopPropagation();
          setOpenActionsFor(null);
          await removeConnection(getId(u));
        }}
      >
        Remove connection
      </button>

      <button
        type="button"
        className="connection-actions-item danger"
        onClick={(e) => {
          e.stopPropagation();
          setOpenActionsFor(null);
          setReportTarget(u);
          setReportReason("");
          setReportError("");
        }}
      >
        Report
      </button>
    </div>
  )}
</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <aside className="connections-sidebar">
            <div className="sidebar-card">
              <div className="sidebar-title pending-title">Pending Connections</div>
              <div className="sidebar-subtitle">Requests you’ve received or sent</div>

              {loading ? (
                <div className="sidebar-empty">Loading...</div>
              ) : incomingPending.length === 0 && outgoingPending.length === 0 ? (
                <div className="sidebar-empty">No pending requests.</div>
              ) : (
                <div className="pending-wrap">
                  {incomingPending.length > 0 && (
                    <div className="pending-group">
                      <div className="pending-group-title">Requests to you</div>
                      <div className="suggested-list">
                        {incomingPending.map((u, idx) => {
                          const id = getId(u);
                          const reqId = incomingRequestIdByUser.get(id);
                          return (
                            <div className="suggested-row" key={id ?? idx}>
                              <div className="suggested-left">
                                <div className="suggested-avatar">
                                  {displayName(u).charAt(0).toUpperCase()}
                                </div>
                                <div className="suggested-text">
                                  <div className="suggested-name">{displayName(u)}</div>
                                  <div className="suggested-meta">
                                    {u.username ? `@${u.username}` : u.email || ""}
                                  </div>
                                </div>
                              </div>

                              <div className="btn-row">
                                <button
                                  className="connection-btn"
                                  onClick={() => requestAccept(reqId)}
                                  disabled={!reqId}
                                >
                                  Accept
                                </button>
                                <button
                                  className="connection-btn danger"
                                  onClick={() => rejectRequest(reqId)}
                                  disabled={!reqId}
                                >
                                  Reject
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {outgoingPending.length > 0 && (
                    <div className="pending-group">
                      <div className="pending-group-title">Requests you sent</div>
                      <div className="suggested-list">
                        {outgoingPending.map((u, idx) => {
                          const id = getId(u);
                          return (
                            <div className="suggested-row" key={id ?? idx}>
                              <div className="suggested-left">
                                <div className="suggested-avatar">
                                  {displayName(u).charAt(0).toUpperCase()}
                                </div>
                                <div className="suggested-text">
                                  <div className="suggested-name">{displayName(u)}</div>
                                  <div className="suggested-meta">
                                    {u.username ? `@${u.username}` : u.email || ""}
                                  </div>
                                </div>
                              </div>

                              <button
                                className="connection-btn secondary"
                                onClick={() => {
                                  cancelOutgoing(id)}
                                }
                                disabled={!id}
                              >
                                Cancel
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="sidebar-divider"></div>

              <div className="sidebar-title">Suggested Friends</div>
              <div className="sidebar-subtitle">
                Ranked by shared preferences ({currentPrefs.length} of yours)
              </div>

              {loading ? (
                <div className="sidebar-empty">Loading...</div>
              ) : suggested.length === 0 ? (
                <div className="sidebar-empty">No suggestions yet.</div>
              ) : (
                <div className="suggested-list">
                  {suggested.map((s, idx) => {
                    const u = s.user;
                    const id = s.userId;
                    return (
                      <div className="suggested-row" key={id ?? idx}>
                        <div className="suggested-left">
                          <div className="suggested-avatar">
                            {displayName(u).charAt(0).toUpperCase()}
                          </div>
                          <div className="suggested-text">
                            <div className="suggested-name">
                              {displayName(u)}
                              {s.score > 0 ? (
                                <span className="match-pill">{s.score} match</span>
                              ) : null}
                            </div>
                            <div className="suggested-meta">
                              {u.username ? `@${u.username}` : u.email || ""}
                            </div>

                            {s.common?.length ? (
                              <div className="common-prefs">
                                {s.common.slice(0, 3).map((p) => (
                                  <span key={p} className="pref-chip">
                                    {p}
                                  </span>
                                ))}
                                {s.common.length > 3 ? (
                                  <span className="pref-chip muted">
                                    +{s.common.length - 3}
                                  </span>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        {/* Intercept ADD */}
                        <button className="connection-btn" onClick={() => requestAdd(id)}>
                          Add
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}